import React from 'react';
import { DollarSign } from 'lucide-react';
import './SpendDrillButton.css';

/**
 * Tiny "$" icon that opens the transaction drill-in for a specific capability
 * or PDP signal. Rendered next to the row name whenever the matching
 * indicator says there's spend in the lookback window or at least one
 * contract still in force — so clicking always reveals something.
 *
 * Props:
 *   indicator — the selected indicator object (hasSpend, hasExpiringContract,
 *     totalSpend, nextExpiration, …). When both hasSpend and
 *     hasExpiringContract are false, this component renders nothing.
 *   onClick — invoked with no args; parent decides what modal to open
 *     (capability vs. signal) based on the caller context.
 *   label — accessible label prefix, e.g. "MFA transactions" →
 *     "View MFA transactions".
 *   size — icon size in px. Defaults to 14 which fits inline inside
 *     pillar-column capability rows.
 */
export default function SpendDrillButton({
  indicator,
  onClick,
  label,
  size = 14,
  className = '',
}) {
  if (!indicator) return null;
  const { hasSpend, hasExpiringContract, totalSpend, nextExpiration } = indicator;
  if (!hasSpend && !hasExpiringContract) return null;

  const title = buildTitle(label, totalSpend, nextExpiration, hasExpiringContract);

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onClick?.();
  };

  return (
    <button
      type="button"
      className={`zta-spend-drill ${hasExpiringContract ? 'zta-spend-drill--expiring' : ''} ${className}`}
      onClick={handleClick}
      onMouseDown={(e) => e.stopPropagation()}
      title={title}
      aria-label={title}
    >
      <DollarSign size={size} strokeWidth={2.5} />
    </button>
  );
}

function buildTitle(label, totalSpend, nextExpiration, hasExpiringContract) {
  const prefix = label ? `View ${label}` : 'View transactions';
  const parts = [prefix];
  if (totalSpend > 0) {
    parts.push(`$${formatCompact(totalSpend)} in last 2y`);
  }
  if (hasExpiringContract && nextExpiration) {
    parts.push(`next expiration ${formatDate(nextExpiration)}`);
  } else if (hasExpiringContract) {
    parts.push('active contract');
  }
  return parts.join(' — ');
}

function formatCompact(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return `${n}`;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
